import { appendResponseHeader } from 'h3';
import { z } from 'zod/v4';

/**
 * Minimal shape of a schema usable for response validation. Any zod schema
 * satisfies this; `.transform()` lets a schema reshape envelopes (e.g.
 * `{ Details: ... }`) while still being the validation boundary.
 */
type ResponseSchema<T> = { parse: (data: unknown) => T };

/** Reduce a request URL to just its path (drop origin + query) for logging. */
function toPath(request: unknown): string {
	if (typeof request !== 'string') return String(request);
	try {
		return new URL(request, 'http://_').pathname;
	} catch {
		return request.split('?')[0] ?? request;
	}
}

export default defineNuxtPlugin(() => {
	const env = import.meta.server ? 'SERVER' : 'CLIENT';

	const runtimeConfig = useRuntimeConfig();

	// Opt-in trace propagation to a cross-origin backend (needs backend CORS allowlist).
	const froggerTraceBackend = Boolean(runtimeConfig.public.froggerTraceBackend);

	// resolveStaticCustomerConfig() returns null when a dev override is in force,
	// so the switcher's tenant beats the baked-in apiOrigin.
	const staticConfig = resolveStaticCustomerConfig();

	let baseURL: string | undefined;
	let portalType: string | undefined;

	if (staticConfig?.apiOrigin) {
		baseURL = staticConfig.apiOrigin;
		portalType = staticConfig.portalType;
	} else {
		// SSR mode: same-origin proxy unless an explicit API_ORIGIN is configured.
		// The tenant still supplies portalType, which gates the refresh path below.
		const { config } = useCustomerConfig();
		baseURL = (runtimeConfig.public.apiOrigin as string | undefined) || config.value?.apiOrigin;
		portalType = config.value?.portalType;
	}

	// Supplier portals use one-time-use logins with no refresh token —
	// attempting a refresh would always fail and trigger a logout.
	const noRefreshToken = portalType === 'supplier';

	const authToken = useState<string | null>('lcp-api-token', () => null);

	const requestHeaders = import.meta.server ? useRequestHeaders(['cookie']) : {};

	const event = import.meta.server ? useRequestEvent() : null;

	// Mutex: if a refresh is already in-flight, piggyback on it instead of
	// firing a second request (the backend rotates tokens, so concurrent
	// refreshes race and the loser gets a 401 → forceLogout).
	let activeRefreshPromise: Promise<string | null> | null = null;

	async function attemptTokenRefresh(): Promise<string | null> {
		if (activeRefreshPromise) {
			return activeRefreshPromise;
		}

		activeRefreshPromise = performTokenRefresh().finally(() => {
			activeRefreshPromise = null;
		});

		return activeRefreshPromise;
	}

	async function performTokenRefresh(): Promise<string | null> {
		if (noRefreshToken) return null;

		try {
			if (import.meta.server) {
				const response = await $fetch.raw<{ accessToken: string }>('/api/Auth/refresh', {
					method: 'POST',
					headers: { ...requestHeaders, ...frogger.getHeaders() },
					ignoreResponseError: true,
				});

				// Forward Set-Cookie regardless of status so the proxy's session-expired flag
				// (set on a rejected refresh) reaches the browser alongside the SSR redirect.
				if (event) {
					const setCookieHeaders = response.headers.getSetCookie?.() || [];
					for (const cookie of setCookieHeaders) {
						appendResponseHeader(event, 'set-cookie', cookie);
					}
				}

				if (response.status >= 200 && response.status < 300) {
					return response._data?.accessToken || null;
				}
				frogger.debug('token refresh rejected', {
					mode: 'server',
					status: response.status,
				});
				return null;
			} else {
				const refreshUrl = staticConfig?.apiOrigin
					? `${staticConfig.apiOrigin}/api/Auth/refresh`
					: '/api/Auth/refresh';
				// Cross-origin (static mode) refresh omits trace headers unless the backend
				// opt-in is on, else the extra header trips the CORS preflight.
				const isCrossOrigin = !!staticConfig?.apiOrigin;
				const traceHeaders =
					froggerTraceBackend || !isCrossOrigin ? frogger.getHeaders() : {};
				const response = await $fetch<{ accessToken: string }>(refreshUrl, {
					method: 'POST',
					credentials: 'include',
					headers: traceHeaders,
				});

				return response.accessToken;
			}
		} catch (error: unknown) {
			const err = error as { status?: number; statusCode?: number; message?: string };
			// Previously swallowed silently — a failed refresh is the invisible cause of
			// prod session death, so surface it (warn: the caller falls back to logout).
			frogger.warn('token refresh request errored', {
				mode: import.meta.server ? 'server' : 'client',
				status: err.status ?? err.statusCode,
				error,
			});
			return null;
		}
	}

	let isForceLoggingOut = false;

	function forceLogout(reason: string) {
		if (!import.meta.client) return;

		// Prevent re-entrant calls within the same page lifecycle
		if (isForceLoggingOut) return;

		// Skip if an intentional logout is already in progress — the logout
		// composable will handle navigation after the API call completes.
		const intentionalLogout = useState<boolean>('lcp-intentional-logout', () => false);
		if (intentionalLogout.value) {
			frogger.debug('forced logout skipped', {
				reason,
				cause: 'intentional-logout-in-progress',
			});
			return;
		}

		// If already on the login page (e.g. after a full reload), 401s are
		// expected because the user isn't authenticated yet — don't redirect.
		if (window.location.pathname === '/login') {
			frogger.debug('forced logout skipped', { reason, cause: 'already-on-login' });
			return;
		}

		isForceLoggingOut = true;
		// The single most important auth signal: an involuntary logout. Reason lets us
		// tell a genuine session death from an expected one at a glance.
		frogger.warn('forced logout', { reason, path: window.location.pathname });
		authToken.value = null;
		const authState = useState<{ isLoggedIn: boolean; userID: string | null }>('lcp-auth');
		if (authState.value) {
			authState.value.isLoggedIn = false;
			authState.value.userID = null;
		}
		const authCheckFailed = useState<boolean>('lcp-auth-check-failed');
		authCheckFailed.value = true;
		navigateTo('/login?logout=true', { external: true });
	}

	// Explicit type annotation (not inferred): the onResponseError 401-retry below calls
	// rawApi() to re-enter the interceptor pipeline, so without this the initializer would
	// reference its own inferred type and TS would collapse rawApi to `any`.
	const rawApi: typeof $fetch = $fetch.create({
		baseURL,
		credentials: 'include',

		onRequest({ request, options }) {
			// In SSR mode on the client, route auth endpoints through the Nuxt server proxy
			// so the proxy can inject the RefreshToken from the encrypted session.
			// Static mode uses absolute URLs (https://...) so this check won't match.
			if (
				import.meta.client &&
				!staticConfig?.apiOrigin &&
				typeof request === 'string' &&
				request.startsWith('/api/Auth/')
			) {
				options.baseURL = '';
			}

			// Per-request child (one child per call) so its failure logs correlate to just this
			// call. The W3C trace headers are NOT guaranteed unique per call: concurrent children
			// snapshot the same parent lastSpanId, so getHeaders() can emit an identical
			// traceparent span-id. The requestId in ctx below is the reliable per-call correlator.
			// Stashed on `options` so the error hooks read back the SAME child + start time.
			const method = String(options.method || 'GET').toUpperCase();
			const path = toPath(request);
			const reqLog = frogger.child({
				context: { requestId: crypto.randomUUID(), method, path },
			});
			(options as unknown as Record<string, unknown>).__froggerLog = reqLog;
			(options as unknown as Record<string, unknown>).__froggerStart = Date.now();

			const existingHeaders =
				options.headers instanceof Headers
					? Object.fromEntries(options.headers.entries())
					: (options.headers as Record<string, string>) || {};

			// Attach W3C trace headers UNLESS this is a browser cross-origin call with the
			// backend-trace opt-in off (that combination needs the backend CORS allowlist or
			// the preflight blocks the request). Server + same-origin proxy always trace.
			const isBrowserCrossOrigin =
				import.meta.client && !!staticConfig?.apiOrigin && options.baseURL !== '';
			const traceHeaders =
				froggerTraceBackend || !isBrowserCrossOrigin ? reqLog.getHeaders() : {};

			//@ts-expect-error
			options.headers = {
				...requestHeaders,
				...existingHeaders,
				...traceHeaders,
				...(authToken.value ? { Authorization: `Bearer ${authToken.value}` } : {}),
			};
		},

		onRequestError({ request, options, error }) {
			// The ONLY place transport-level failures (DNS, CORS, offline, abort) are seen —
			// they never reach onResponseError because there is no response.
			const reqLog =
				((options as unknown as Record<string, unknown>).__froggerLog as
					| typeof frogger
					| undefined) ?? frogger;
			const start = (options as unknown as Record<string, unknown>).__froggerStart as
				| number
				| undefined;
			reqLog.error('API request network error', {
				method: String(options.method || 'GET').toUpperCase(),
				path: toPath(request),
				durationMs: start ? Date.now() - start : undefined,
				error,
			});
		},

		async onResponseError({ request, response, options }) {
			const status = response.status;

			// Structured failure log on the request's own span. 401 stays at debug (it's the
			// expected refresh path, logged as an event by forceLogout only if it escalates).
			const reqLog =
				((options as unknown as Record<string, unknown>).__froggerLog as
					| typeof frogger
					| undefined) ?? frogger;
			const start = (options as unknown as Record<string, unknown>).__froggerStart as
				| number
				| undefined;
			const failCtx = {
				method: String(options.method || 'GET').toUpperCase(),
				path: toPath(request),
				status,
				durationMs: start ? Date.now() - start : undefined,
			};
			if (status >= 500) reqLog.error('API request failed', failCtx);
			else if (status === 401) reqLog.debug('API request 401 (auth refresh path)', failCtx);
			else if (status === 403) reqLog.warn('API request forbidden', failCtx);
			else if (status >= 400) reqLog.warn('API request failed', failCtx);

			if (status === 401) {
				// Prevent infinite retry: if this is already a retry, don't retry again
				if ((options as any).__isRetry) {
					forceLogout('401-retry-exhausted');
					return;
				}

				// Mapping/supplier portals have no refresh token — skip
				// the refresh attempt and go straight to logout.
				if (noRefreshToken) {
					forceLogout('no-refresh-token');
					return;
				}

				const newToken = await attemptTokenRefresh();

				if (newToken) {
					authToken.value = newToken;

					// Strip stale Authorization from headers — the onRequest interceptor
					// (carried via ...options) will add the correct one from authToken.value
					//@ts-expect-error
					const existingHeaders = (options.headers || {}) as Record<string, string>;
					const { authorization, Authorization, ...cleanHeaders } = existingHeaders;

					return rawApi(request, {
						...options,
						//@ts-expect-error
						__isRetry: true,
						headers: cleanHeaders,
					});
				}

				forceLogout('refresh-failed');
				return;
			}

			if (status === 403) {
				const data = response._data as { message?: string };
				if (data?.message === 'Malformed session') {
					forceLogout('403-malformed-session');
					return;
				}
			}
		},
	});

	// Schema-aware wrapper around the ofetch instance. This is the single
	// validation boundary: anything reaching the rest of the app via `$api`
	// with a `schema` has been parsed and is guaranteed to match. Auth/refresh/
	// retry all live in `rawApi`'s interceptors and resolve before we validate.
	//
	// The public type intersects an inferring `schema` overload with `rawApi`'s
	// own signature, so existing schema-less call sites keep their exact ofetch
	// typing and `$api(url, { schema })` infers its return type from the schema.
	type RawApiOptions = NonNullable<Parameters<typeof rawApi>[1]>;
	type ApiFetch = {
		<T>(request: string, options: RawApiOptions & { schema: ResponseSchema<T> }): Promise<T>;
	} & typeof rawApi;

	const api = ((
		request: string,
		options?: RawApiOptions & { schema?: ResponseSchema<unknown> },
	) => {
		if (!options?.schema) {
			return rawApi(request, options);
		}

		const { schema, ...fetchOptions } = options;

		return rawApi(request, fetchOptions).then((data) => {
			try {
				return schema.parse(data);
			} catch (err) {
				if (err instanceof z.ZodError) {
					// A schema mismatch means the backend contract drifted — a real defect,
					// not user error. Keep the path static; put the prettified issues in ctx.
					frogger.error('API response validation failed', {
						path: toPath(request),
						issues: z.prettifyError(err),
					});
				}
				throw err;
			}
		});
	}) as unknown as ApiFetch;

	const authenticate = (token: string) => {
		authToken.value = token;
	};

	const deauthenticate = () => {
		authToken.value = null;
	};

	return {
		provide: {
			api,
			apiAuth: {
				authenticate,
				deauthenticate,
				getToken: () => authToken.value,
				attemptTokenRefresh,
				// Exposed so session-death recovery paths outside the interceptor
				// (ChangeUser 400s, idle-wake probes) reuse the same guarded logout.
				forceLogout,
			},
		},
	};
});
